import { subDays } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import type { ISODateString } from "@visioni/a3s-codecs";

/**
 * Default window for timelapse export requests: a rolling seven camera-local days.
 *
 * Boundaries are computed in the camera's timezone (so a viewer in a different
 * timezone never drops the most recent frame), then converted to UTC ISO
 * strings for the API.
 *
 * - End: exact current camera-local instant
 * - Start: exactly 7 camera-local days before that end instant
 *
 * The hard cap on the wire schema is 14 days; we deliberately request only 7
 * here so the export pipeline and viewer payloads stay small in the default UX
 * while still leaving headroom for future date pickers.
 */
export const TIMELAPSE_DEFAULT_DAYS = 7;

export interface TimelapseRange {
  readonly startTime: ISODateString;
  readonly endTime: ISODateString;
}

export function computeDefaultTimelapseRange(now: Date, cameraTimezone: string): TimelapseRange {
  const zonedEnd = toZonedTime(now, cameraTimezone);
  const zonedStart = subDays(zonedEnd, TIMELAPSE_DEFAULT_DAYS);

  return {
    startTime: fromZonedTime(zonedStart, cameraTimezone).toISOString() as ISODateString,
    endTime: fromZonedTime(zonedEnd, cameraTimezone).toISOString() as ISODateString,
  };
}
